import { ThreadType } from "./switch-core/types";
import {
  conversationProjection,
  createDmConversationId,
  createGroupConversationId,
  createSelfConversationId,
  type ConversationProjection,
} from "./switch-core";

export interface ThreadKeyInput {
  groupId?: string;
  senderId?: string;
  receiverId?: string;
  threadKey?: string;
  threadType?: ThreadType;
  withUserId?: string;
}

export interface ThreadKeyInfo {
  threadKey: string | undefined;
  threadType: ThreadType | undefined;
}

export function createGroupThreadKey(groupId: string): string {
  return createGroupConversationId(groupId);
}

export function createSelfThreadKey(userId: string): string {
  return createSelfConversationId(userId);
}

export function createDirectThreadKey(senderId: string, receiverId: string): string {
  if (senderId === receiverId) {
    return createSelfThreadKey(senderId);
  }

  return createDmConversationId(senderId, receiverId);
}

export function resolveThreadInfo(input: ThreadKeyInput): ThreadKeyInfo {
  const hintedThreadType = input.threadKey
    ? inferThreadTypeFromKey(input.threadKey)
    : undefined;

  if (input.groupId || hintedThreadType === ThreadType.GROUP) {
    const groupId = input.groupId || groupIdFromThreadKey(input.threadKey);
    return {
      threadKey: groupId ? createGroupThreadKey(groupId) : input.threadKey,
      threadType: input.threadType || ThreadType.GROUP,
    };
  }

  if (input.senderId && input.receiverId) {
    const isSelf = input.senderId === input.receiverId;
    return {
      threadKey: createDirectThreadKey(input.senderId, input.receiverId),
      threadType: input.threadType || (isSelf ? ThreadType.SELF : ThreadType.DM),
    };
  }

  if (input.threadKey) {
    return {
      threadKey: input.threadKey,
      threadType: input.threadType || hintedThreadType,
    };
  }

  return {
    threadKey: input.withUserId,
    threadType: input.threadType,
  };
}

export function inferThreadTypeFromKey(threadKey: string): ThreadType | undefined {
  if (threadKey.startsWith("group:")) return ThreadType.GROUP;
  if (threadKey.startsWith("self:")) return ThreadType.SELF;
  if (threadKey.startsWith("dm:")) return ThreadType.DM;
  return undefined;
}

export function groupIdFromThreadKey(threadKey?: string): string | undefined {
  if (!threadKey?.startsWith("group:")) return undefined;
  return threadKey.slice("group:".length);
}

export function defaultConversationProjection(input: {
  kind?: string | null;
  conversationId?: string | null;
  communityId?: string | null;
  currentUserId?: string | null;
  withUserId?: string | null;
}): ConversationProjection {
  return conversationProjection(
    input.kind,
    input.conversationId,
    input.communityId,
    input.currentUserId,
    input.withUserId
  );
}
